import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Layers, Plus } from "lucide-react";
import { PATHS } from "@/lib/router";
import { customFrameworkStore } from "../stores/custom-framework.store";
import { DisclosureTable } from "../components/DisclosureTable";
import { DisclosureFormDialog } from "../components/DisclosureFormDialog";
import { PageHeader, ConfirmDialog } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { showToast } from "@/lib/utils/toast";

const store = customFrameworkStore;

export const CustomFrameworkBuilderPage = observer(
  function CustomFrameworkBuilderPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingDisclosure, setEditingDisclosure] = useState(null);
    const [deleteDisclosure, setDeleteDisclosure] = useState(null);

    useEffect(() => {
      store.fetchFramework(id);
      store.fetchDisclosures(id);
      return () => store.clearDisclosures();
    }, [id]);

    const handleOpenAdd = () => {
      setEditingDisclosure(null);
      setDialogOpen(true);
    };

    const handleOpenEdit = (disclosure) => {
      setEditingDisclosure(disclosure);
      setDialogOpen(true);
    };

    const handleDisclosureSubmit = async (values) => {
      const isEdit = Boolean(editingDisclosure);
      const promise = isEdit
        ? store.updateDisclosure(id, editingDisclosure._id, values)
        : store.addDisclosure(id, values);
      showToast.promise(promise, {
        loading: isEdit ? "Updating disclosure..." : "Adding disclosure...",
        success: isEdit ? "Disclosure updated" : "Disclosure added",
        error: (err) => err?.message || "Failed to save disclosure",
      });
      await promise;
    };

    const handleDeleteDisclosure = async () => {
      if (!deleteDisclosure) return;
      const promise = store.deleteDisclosure(id, deleteDisclosure._id);
      showToast.promise(promise, {
        loading: "Deleting disclosure...",
        success: "Disclosure deleted",
        error: (err) => err?.message || "Failed to delete disclosure",
      });
      await promise;
      setDeleteDisclosure(null);
    };

    const fw = store.selectedFramework;

    return (
      <div className="flex-1 flex flex-col min-h-0 gap-y-6">
        <button
          type="button"
          className="flex w-fit items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
          onClick={() => navigate(PATHS.CUSTOM_FRAMEWORK_LIST)}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Custom Frameworks
        </button>

        {store.loading && !fw ? (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Skeleton className="h-7 w-7 rounded-lg" />
              <div className="space-y-2">
                <Skeleton className="h-5 w-48" />
                <Skeleton className="h-3 w-72" />
              </div>
            </div>
            <Skeleton className="h-9 w-36 rounded-md" />
          </div>
        ) : (
          <PageHeader
            title={fw?.name || "Framework Builder"}
            description={
              fw?.description || "Manage disclosures for this framework."
            }
            icon={Layers}
            actions={
              <Button type="button" onClick={handleOpenAdd}>
                <Plus className="mr-1.5 h-4 w-4" />
                Add Metric
              </Button>
            }
          />
        )}

        {store.disclosuresLoading ? (
          <div className="space-y-2">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-lg border border-border p-4">
                <div className="flex items-start gap-3">
                  <Skeleton className="h-10 w-10 shrink-0 rounded-lg" />
                  <div className="flex flex-1 flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <Skeleton className="h-5 w-16 rounded" />
                      <Skeleton className="h-4 w-48" />
                    </div>
                    <Skeleton className="h-3 w-36" />
                  </div>
                  <div className="flex gap-1">
                    <Skeleton className="h-8 w-8 rounded-md" />
                    <Skeleton className="h-8 w-8 rounded-md" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <DisclosureTable
            disclosures={store.disclosures}
            onEdit={handleOpenEdit}
            onDelete={setDeleteDisclosure}
          />
        )}

        <DisclosureFormDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={handleDisclosureSubmit}
          initial={editingDisclosure}
          metrics={store.disclosures.filter(
            (d) => d._id !== editingDisclosure?._id && d.isQuantitative,
          )}
        />

        <ConfirmDialog
          open={Boolean(deleteDisclosure)}
          onOpenChange={(v) => !v && setDeleteDisclosure(null)}
          title="Delete Disclosure?"
          description={`"${deleteDisclosure?.code} — ${deleteDisclosure?.title}" will be deactivated. Existing data entries will not be affected.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          tone="danger"
          onConfirm={handleDeleteDisclosure}
        />
      </div>
    );
  },
);
